import type { NitroFetchOptions, NitroFetchRequest } from 'nitropack';

/**
 * useGetData:
 * This function is used to handle fetching data from the server
 * It takes the request and the options for the fetch
 * It returns the data and the error
 *
 * @param {NitroFetchRequest} request: The request to fetch the data
 * @param {NitroFetchOptions} opts: The options for the fetch
 * @returns {pending, data, error, execute}
 *
 * @example
 * const { pending, data, error, execute } = useGetData<T, Error>();
 *
 * onMounted(() => {
 *   execute({ url: 'https://api.example.com/data' });
 * });
 */
export function useGetData<T, Error = unknown>() {
  const pending = ref(false);
  const data = ref<T>();
  const error = ref<Error>();
  const nuxtApp = useNuxtApp();
  const { $api } = nuxtApp;

  async function execute(request: NitroFetchRequest, opts?: NitroFetchOptions<NitroFetchRequest, 'get'> | undefined) {
    try {
      error.value = undefined;
      pending.value = true;
      const response = await $api<T>(request, {
        ...opts,
        immediate: false,
        server: false,
        getCachedData: (key: string) => nuxtApp.static.data[key] ?? nuxtApp.payload.data[key],
      });
      data.value = response as T;
    } catch (e) {
      error.value = e as Error;
    } finally {
      pending.value = false;
    }
  }

  return {
    pending,
    data,
    error,
    execute,
  };
}
